import React from "react";
import PropTypes from "prop-types";
import {useTheme} from "../../../theme/ThemeProvider";
import {buildStyles} from "../../buildStyles";
import Container from "../../layout/Container";
import FlexContainer from "../../layout/FlexContainer";
import Text from "../Text";
import SvgIcon from "../SvgIcon";
import ICONS from "../../../icons/iconMap";
import InfoCardBase from "./base/InfoCardBase";
import {generateStyles} from "../../../theme/styleUtilities";

const useStyles = (theme) =>
  buildStyles({
    container: {
      display: "flex",
      alignItems: "flex-start",
      width: "100%",
      position: "relative",
      paddingLeft: theme.spacing(3),
    },
    leftBorder: {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "5px",
      backgroundColor: "var(--card-icon-color, currentColor)",
      borderRadius: "5px",
    },
    iconContainer: {
      flexShrink: 0,
    },
    content: {
      flex: 1,
      minWidth: 0,
    },
  });

/**
 * InfoCard is a component that renders an informational card with an icon and content.
 * It works with any cardBase component without any internal styling logic.
 *
 * @param {Object} props - The props for the InfoCard component.
 * @param {React.ElementType} [props.cardBase=InfoCardBase] - The component to render for the card base.
 * @param {React.ReactNode} [props.icon] - Custom icon to display. Defaults to info icon if not provided.
 * @param {React.ReactNode} [props.title] - The title text for the card.
 * @param {React.ReactNode} [props.description] - The description text for the card.
 * @param {React.ReactNode} [props.children] - Additional content to display in the card.
 * @param {boolean} [props.showBorder=true] - Whether to show the colored left border indicator.
 * @param {Object} [props.sx={}] - The style object that can be used to override default card styles.
 * @param {function} [props.onClick] - Callback when the card is clicked.
 * @param {Object} props.rest - Any other props that should be passed to the card base component.
 *
 * @returns {JSX.Element} The rendered InfoCard component.
 */
const InfoCard = ({
  cardBase: CardBaseComponent = InfoCardBase,
  icon,
  title,
  description,
  children,
  showBorder = true,
  sx = {},
  onClick,
  ...props
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);

  const defaultStyles = {
    alignItems: "flex-start",
    cursor: onClick ? "pointer" : "default",
  };
  const styles = generateStyles(
    {sx: {...defaultStyles, ...sx}, ...props},
    theme,
  );

  const iconElement = icon || (
    <SvgIcon
      component={ICONS["info"]}
      sx={{
        width: "16px",
        height: "16px",
        color: "var(--card-icon-color, currentColor)",
      }}
    />
  );

  return (
    <CardBaseComponent sx={styles} onClick={onClick} {...props}>
      <FlexContainer
        className={classes.container}
        alignItems="flex-start"
        sx={{
          paddingLeft: theme.spacing(3),
        }}
      >
        {showBorder && <Container className={classes.leftBorder} />}
        <Container className={classes.content}>
          {title && (
            <FlexContainer
              alignItems="center"
              gap={theme.spacing(1)}
              sx={{
                marginBottom: description || children ? theme.spacing(0.5) : 0,
              }}
            >
              <FlexContainer
                className={classes.iconContainer}
                alignItems="center"
                justifyContent="center"
              >
                {iconElement}
              </FlexContainer>
              <Text
                variant="textMD"
                weight="semibold"
                sx={{
                  color: "var(--card-title-color, inherit)",
                }}
              >
                {title}
              </Text>
            </FlexContainer>
          )}

          {description && (
            <FlexContainer
              alignItems="center"
              gap={theme.spacing(2)}
              sx={{marginBottom: children ? theme.spacing(2) : 0}}
            >
              {!title && (
                <FlexContainer
                  className={classes.iconContainer}
                  alignItems="center"
                  justifyContent="center"
                >
                  {iconElement}
                </FlexContainer>
              )}
              <Text
                variant="textXS"
                sx={{
                  color:
                    "var(--card-description-color, var(--card-title-color, inherit))",
                  opacity: title ? 0.9 : 1,
                }}
              >
                {description}
              </Text>
            </FlexContainer>
          )}

          {children && (
            <Container sx={{width: "100%", marginLeft: theme.spacing(4.5)}}>
              {children}
            </Container>
          )}
        </Container>
      </FlexContainer>
    </CardBaseComponent>
  );
};

InfoCard.propTypes = {
  cardBase: PropTypes.any,
  icon: PropTypes.node,
  title: PropTypes.node,
  description: PropTypes.node,
  children: PropTypes.node,
  showBorder: PropTypes.bool,
  onClick: PropTypes.func,
  sx: PropTypes.object,
};

export default InfoCard;
